import {
  autoUpdate,
  computePosition,
  flip,
  hide,
  offset,
  shift,
  size,
} from "@floating-ui/dom";
import { createEffect, createSignal, onCleanup, splitProps } from "solid-js";
import { Portal } from "solid-js/web";

import { floatingHost } from "../core/anchor";

/** Smallest gap kept between a floating element and the screen edge, in pixels. */
const DEFAULT_PADDING = 8;

/**
 * @typedef {object} FloatingProps
 * @property {import('../core/anchor').FloatingAnchor | null | undefined} reference - What the element floats next to
 * @property {import('@floating-ui/dom').Placement} [placement='bottom-start'] - Preferred side and alignment
 * @property {number} [offset=0] - Gap between the reference and the element, in pixels
 * @property {number} [padding=8] - Gap kept from the screen edge, in pixels
 * @property {boolean} [matchWidth] - Size the element to the reference's width
 * @property {number} [maxHeight] - Height cap in pixels, lowered further to the space available
 * @property {boolean} [hideWhenDetached=true] - Hide the element while its reference is scrolled out of view
 * @property {() => void} [onDismiss] - Called on a press outside the reference and element, or on Escape
 * @property {string} [className] - Classes for the floating element
 * @property {(element: HTMLElement) => void} [ref] - Receives the floating element
 * @property {import('solid-js').JSX.Element} children - Floating content
 *
 * Any other attributes, such as `data-*` hooks, are set on the floating element.
 */

/**
 * @typedef {object} MiddlewareOptions
 * @property {number} offset - Gap between the reference and the element
 * @property {number} padding - Gap kept from the screen edge
 * @property {boolean} matchWidth - Size the element to the reference's width
 * @property {number | undefined} maxHeight - Height cap in pixels
 * @property {boolean} hideWhenDetached - Hide while the reference is out of view
 */

/**
 * Build the middleware chain for one position computation.
 * @param {MiddlewareOptions} options Resolved positioning options
 * @returns {import('@floating-ui/dom').Middleware[]} Middleware in application order
 */
function buildMiddleware(options) {
  const { padding, matchWidth, maxHeight } = options;
  const middleware = [
    offset(options.offset),
    flip({ padding }),
    shift({ padding }),
    size({
      padding,
      apply({ availableWidth, availableHeight, rects, elements }) {
        const style = elements.floating.style;
        style.maxWidth = `${availableWidth}px`;
        if (matchWidth) style.width = `${rects.reference.width}px`;
        if (maxHeight !== undefined) {
          style.maxHeight = `${Math.min(availableHeight, maxHeight)}px`;
        }
      },
    }),
  ];
  if (options.hideWhenDetached) middleware.push(hide());
  return middleware;
}

/**
 * Whether a DOM event target sits inside the element or, when it is a real
 * element, the reference.
 * @param {EventTarget | null} target Event target
 * @param {HTMLElement} element Floating element
 * @param {import('../core/anchor').FloatingAnchor} reference Reference
 * @returns {boolean} True when the event came from inside either
 */
function isInside(target, element, reference) {
  if (!(target instanceof Node)) return false;
  if (element.contains(target)) return true;
  return reference instanceof Element && reference.contains(target);
}

/**
 * A floating element anchored to a reference and kept on the visible screen.
 * It flips to the other side when its preferred side has no room, slides along
 * the screen edge, shrinks to the space available, and follows the reference
 * through scrolling, resizing and layout shifts. The visible screen is the
 * visual viewport, so an on-screen keyboard counts as the bottom edge.
 * @param {FloatingProps} props Component props
 * @returns {import('solid-js').JSX.Element} Floating element
 */
export function Floating(props) {
  const [local, attributes] = splitProps(props, [
    "reference",
    "placement",
    "offset",
    "padding",
    "matchWidth",
    "maxHeight",
    "hideWhenDetached",
    "onDismiss",
    "className",
    "ref",
    "children",
  ]);
  const [element, setElement] = createSignal(null);
  const [position, setPosition] = createSignal(null);

  createEffect(() => {
    const reference = local.reference;
    const floating = element();
    if (!reference || !floating) return;

    const placement = local.placement ?? "bottom-start";
    const middleware = buildMiddleware({
      offset: local.offset ?? 0,
      padding: local.padding ?? DEFAULT_PADDING,
      matchWidth: local.matchWidth === true,
      maxHeight: local.maxHeight,
      hideWhenDetached: local.hideWhenDetached !== false,
    });
    let isActive = true;

    const update = () => {
      computePosition(reference, floating, {
        strategy: "fixed",
        placement,
        middleware,
      }).then(({ x, y, middlewareData }) => {
        if (!isActive) return;
        setPosition({
          x,
          y,
          isDetached: middlewareData.hide?.referenceHidden === true,
        });
      });
    };

    const stopAutoUpdate = autoUpdate(reference, floating, update);
    const viewport = window.visualViewport;
    viewport?.addEventListener("resize", update);
    viewport?.addEventListener("scroll", update);

    onCleanup(() => {
      isActive = false;
      stopAutoUpdate();
      viewport?.removeEventListener("resize", update);
      viewport?.removeEventListener("scroll", update);
    });
  });

  createEffect(() => {
    const onDismiss = local.onDismiss;
    const reference = local.reference;
    const floating = element();
    if (!onDismiss || !reference || !floating) return;

    const handlePointerDown = (e) => {
      if (!isInside(e.target, floating, reference)) onDismiss();
    };
    // Stopped here so an open menu inside a modal does not also close the modal.
    const handleKeyDown = (e) => {
      if (e.key !== "Escape") return;
      e.stopPropagation();
      e.preventDefault();
      onDismiss();
    };

    document.addEventListener("pointerdown", handlePointerDown, true);
    document.addEventListener("keydown", handleKeyDown, true);
    onCleanup(() => {
      document.removeEventListener("pointerdown", handlePointerDown, true);
      document.removeEventListener("keydown", handleKeyDown, true);
    });
  });

  return (
    <Portal
      mount={floatingHost(local.reference)}
      ref={(container) => {
        container.style.display = "contents";
      }}
    >
      <div
        {...attributes}
        ref={(node) => {
          setElement(node);
          local.ref?.(node);
        }}
        className={local.className}
        style={{
          position: "fixed",
          top: `${position()?.y ?? 0}px`,
          left: `${position()?.x ?? 0}px`,
          visibility:
            position() && !position().isDetached ? "visible" : "hidden",
        }}
      >
        {local.children}
      </div>
    </Portal>
  );
}
